import React, { useEffect, useState } from 'react';
import { HONOR_CODE_LINES, HONOR_CODE_SUBLINE } from '../../constants/honorCode';
import { formatCommunityResultDisplay } from '../../utils/communityResultDisplay';
import {
	fieldLabelClass,
	hintClass,
	inputClass,
	nestedPanelClass
} from './challengeWireFormStyles';

function inputLabelFor(meta) {
	return meta?.result_input_label || meta?.log_entry_label || 'Your result';
}

function submitLabelFor(meta) {
	return meta?.submit_button_label || 'Submit';
}

function currentResultPrefix(meta) {
	return meta?.current_result_label || 'Your current best: ';
}

const MILES_BUTTON_STEP = 0.5;
const MILES_TOTAL_MIN = 0.1;
const MILES_INPUT_STEP = 0.1;

function formatMilesInput(number) {
	if (number == null || Number.isNaN(number)) return '';
	return String(Math.round(number * 10) / 10);
}

function MilesTotalInput({ value, onChange, disabled }) {
	function adjust(delta) {
		const current = parseFloat(value);
		const base = Number.isNaN(current) ? 0 : current;
		const next = Math.max(MILES_TOTAL_MIN, Math.round((base + delta) * 10) / 10);
		onChange(formatMilesInput(next));
	}

	const stepperBtnClass =
		'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg text-base font-semibold text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50';

	return (
		<div className="mt-2 flex items-stretch gap-3">
			<input
				type="number"
				inputMode="decimal"
				step={MILES_INPUT_STEP}
				min={MILES_TOTAL_MIN}
				className={`number-input-no-spinner ${inputClass} min-h-0 min-w-0 flex-1 py-3 text-center text-4xl font-bold tabular-nums text-gray-900 dark:text-dark-text`}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				disabled={disabled}
				aria-label="Total miles"
			/>
			<div className="flex shrink-0 flex-col gap-1">
				<button
					type="button"
					onClick={() => adjust(MILES_BUTTON_STEP)}
					disabled={disabled}
					aria-label="Increase miles by 0.5"
					className={stepperBtnClass}
				>
					+
				</button>
				<button
					type="button"
					onClick={() => adjust(-MILES_BUTTON_STEP)}
					disabled={disabled}
					aria-label="Decrease miles by 0.5"
					className={stepperBtnClass}
				>
					−
				</button>
			</div>
		</div>
	);
}

function HonorCodeBlockquote() {
	return (
		<blockquote className="mt-2 border-l-4 border-blue-600 pl-4 text-sm leading-relaxed text-gray-800 dark:border-blue-500 dark:text-dark-text">
			{HONOR_CODE_LINES.map((line) => (
				<p key={line}>{line}</p>
			))}
		</blockquote>
	);
}

export default function CommunityResultModal({
	isOpen,
	onClose,
	onConfirm,
	templateMeta,
	currentBest,
	currentResultValue = null,
	loading = false,
	error = ''
}) {
	const [agreed, setAgreed] = useState(false);
	const [value, setValue] = useState('');
	const [minutes, setMinutes] = useState('');
	const [seconds, setSeconds] = useState('');
	const [note, setNote] = useState('');

	const isDuration = templateMeta?.result_input_type === 'duration';
	const isMilesTotal = templateMeta?.result_input_type === 'miles';
	const isAccumulate = templateMeta?.result_entry_mode === 'accumulate_during_window';
	const canConfirm = agreed && !loading;

	useEffect(() => {
		if (isOpen) {
			setAgreed(false);
			setValue(
				isMilesTotal && currentResultValue != null
					? formatMilesInput(Number(currentResultValue))
					: ''
			);
			setMinutes('');
			setSeconds('');
			setNote('');
		}
	}, [isOpen, isMilesTotal, currentResultValue]);

	if (!isOpen) return null;

	function handleClose() {
		if (loading) return;
		onClose();
	}

	function resolvedValue() {
		if (isDuration) return `${minutes || 0}:${String(seconds || 0).padStart(2, '0')}`;
		return value;
	}

	function handleConfirm() {
		if (!canConfirm) return;
		const resultValue = resolvedValue();
		if (!resultValue && resultValue !== 0) return;
		onConfirm({ resultValue, note: note.trim() || undefined });
	}

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 dark:bg-black dark:bg-opacity-75 px-4"
			onClick={handleClose}
			role="presentation"
		>
			<div
				className="w-full max-w-md rounded-2xl border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card p-5 shadow-xl text-gray-900 dark:text-dark-text"
				onClick={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-labelledby="community-result-heading"
			>
				<h2 id="community-result-heading" className="text-lg font-semibold text-gray-900 dark:text-dark-text">
					{templateMeta?.result_modal_title || (isAccumulate ? 'Log entry' : 'Submit result')}
				</h2>

				{currentBest && (
					<p className={`mt-2 ${hintClass}`}>
						{currentResultPrefix(templateMeta)}
						<span className="font-semibold text-gray-900 dark:text-dark-text">
							{formatCommunityResultDisplay(currentBest)}
						</span>
					</p>
				)}

				<div className={`mt-4 ${nestedPanelClass}`}>
					<label className="block">
						<span className={fieldLabelClass}>{inputLabelFor(templateMeta)}</span>
						{isDuration ? (
							<div className="mt-2 flex items-center gap-2">
								<input
									type="number"
									min="0"
									className={`${inputClass} w-20 text-center`}
									value={minutes}
									onChange={(e) => setMinutes(e.target.value)}
									placeholder="min"
									aria-label="Minutes"
								/>
								<span className="text-lg font-semibold text-gray-500 dark:text-gray-400" aria-hidden="true">
									:
								</span>
								<input
									type="number"
									min="0"
									max="59"
									className={`${inputClass} w-20 text-center`}
									value={seconds}
									onChange={(e) => setSeconds(e.target.value)}
									placeholder="sec"
									aria-label="Seconds"
								/>
							</div>
						) : isMilesTotal ? (
							<MilesTotalInput value={value} onChange={setValue} disabled={loading} />
						) : (
							<input
								type="text"
								inputMode="decimal"
								className={`${inputClass} mt-2`}
								value={value}
								onChange={(e) => setValue(e.target.value)}
							/>
						)}
					</label>

					{isAccumulate && (
						<label className="block">
							<span className={fieldLabelClass}>Note (optional)</span>
							<input
								type="text"
								className={`${inputClass} mt-2`}
								value={note}
								onChange={(e) => setNote(e.target.value)}
							/>
						</label>
					)}
				</div>

				<div className="mt-4">
					<p className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary">Repeat after me:</p>
					<p className="mt-2 text-xs uppercase tracking-wide text-gray-500 dark:text-dark-text-secondary">
						{HONOR_CODE_SUBLINE}
					</p>
					<HonorCodeBlockquote />
				</div>

				<label className="mt-4 flex items-start gap-3 cursor-pointer">
					<input
						type="checkbox"
						checked={agreed}
						onChange={(e) => setAgreed(e.target.checked)}
						disabled={loading}
						className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-dark-bg dark:focus:ring-blue-400"
					/>
					<span className="text-sm text-gray-700 dark:text-dark-text-secondary">I agree</span>
				</label>

				{error && (
					<p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
						{error}
					</p>
				)}

				<div className="mt-5 flex justify-end gap-3 border-t border-gray-200 dark:border-dark-border pt-4">
					<button
						type="button"
						onClick={handleClose}
						disabled={loading}
						className="rounded-xl border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={handleConfirm}
						disabled={!canConfirm}
						aria-disabled={!canConfirm}
						className={
							canConfirm
								? 'rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700'
								: 'rounded-xl bg-gray-300 dark:bg-gray-600 px-4 py-2.5 text-sm font-semibold text-gray-500 dark:text-gray-400 cursor-not-allowed'
						}
					>
						{loading ? 'Saving…' : submitLabelFor(templateMeta)}
					</button>
				</div>
			</div>
		</div>
	);
}
